import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import RichTextEditor from "./RichTextEditor";
import { bodyToHtml } from "../lib/sanitizeHtml";
import {
  Plus, FilePlus, UploadSimple, ArrowUp, ArrowDown, Trash, TextH, TextT, ListBullets,
  PaperPlaneTilt, SealCheck, RocketLaunch, ArrowUUpLeft, GitDiff, ClockCounterClockwise, Sparkle, MagicWand,
} from "@phosphor-icons/react";

const KINDS = ["policy", "procedure", "statement", "guidance", "privacy", "toolbox_talk", "rams"];
const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600", review: "bg-amber-100 text-amber-800",
  approved: "bg-blue-100 text-blue-800", published: "bg-emerald-100 text-emerald-800",
  superseded: "bg-slate-100 text-slate-400",
};

function StatusChip({ s }) {
  return <span className={`text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 ${STATUS_STYLE[s] || "bg-slate-100 text-slate-500"}`}>{s}</span>;
}

// -------- block editor --------
function BlockEditor({ blocks, onChange, disabled }) {
  const set = (i, patch) => onChange(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const copy = [...blocks];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  const add = (type) => onChange([...blocks, type === "list" ? { type, items: [""] } : { type, text: "" }]);
  const remove = (i) => onChange(blocks.filter((_, j) => j !== i));

  return (
    <div className="flex flex-col gap-3" data-testid="block-editor">
      {blocks.length === 0 && <p className="text-sm text-slate-400 italic">No content yet. Add a heading, paragraph or list below.</p>}
      {blocks.map((b, i) => (
        <div key={i} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`block-${i}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{b.type}</span>
            {!disabled && (
              <div className="flex items-center gap-1">
                <button data-testid={`block-up-${i}`} onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-slate-700"><ArrowUp size={14} /></button>
                <button data-testid={`block-down-${i}`} onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-slate-700"><ArrowDown size={14} /></button>
                <button data-testid={`block-del-${i}`} onClick={() => remove(i)} className="p-1 text-slate-400 hover:text-red-600"><Trash size={14} /></button>
              </div>
            )}
          </div>
          {b.type === "list" ? (
            <Textarea data-testid={`block-input-${i}`} disabled={disabled} value={(b.items || []).join("\n")}
              onChange={(e) => set(i, { items: e.target.value.split("\n") })}
              placeholder="One bullet per line" className="min-h-[80px] text-sm" />
          ) : (
            <Textarea data-testid={`block-input-${i}`} disabled={disabled} value={b.text || ""}
              onChange={(e) => set(i, { text: e.target.value })}
              placeholder={b.type === "heading" ? "Section heading" : "Paragraph text"}
              className={`text-sm ${b.type === "heading" ? "font-bold min-h-[44px]" : "min-h-[70px]"}`} />
          )}
        </div>
      ))}
      {!disabled && (
        <div className="flex gap-2">
          <Button data-testid="add-heading" type="button" variant="outline" size="sm" onClick={() => add("heading")} className="gap-1"><TextH size={14} /> Heading</Button>
          <Button data-testid="add-paragraph" type="button" variant="outline" size="sm" onClick={() => add("paragraph")} className="gap-1"><TextT size={14} /> Paragraph</Button>
          <Button data-testid="add-list" type="button" variant="outline" size="sm" onClick={() => add("list")} className="gap-1"><ListBullets size={14} /> List</Button>
        </div>
      )}
    </div>
  );
}

// -------- step 1: how do you want to create it? --------
function CreateChooser({ open, onClose, onPick }) {
  const CARD = ({ tid, icon, title, desc, mode }) => (
    <button data-testid={tid} onClick={() => onPick(mode)}
      className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors">
      <div className="flex items-center gap-2 mb-1 text-emerald-700">{icon}<span className="font-display font-bold text-slate-900">{title}</span></div>
      <p className="text-sm text-slate-500">{desc}</p>
    </button>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-chooser">
        <DialogHeader><DialogTitle>Create a policy</DialogTitle>
          <DialogDescription>Pick how you want to start. Everything begins as an unassigned draft — nothing goes to staff until you publish.</DialogDescription></DialogHeader>
        <div className="flex flex-col gap-2.5">
          <CARD tid="choose-ai" mode="ai" icon={<Sparkle size={18} />} title="Create with AI" desc="Answer a few questions about your practices; AI prepares an editable draft." />
          <CARD tid="choose-import" mode="import-file" icon={<UploadSimple size={18} />} title="Import a Word or PDF" desc="Upload a .docx/.pdf. The original is retained; text is extracted into a draft." />
          <CARD tid="choose-blank" mode="new" icon={<FilePlus size={18} />} title="Blank / template" desc="Start from an empty rich-text document and write it yourself." />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- blank / AI create --------
function CreateDialog({ mode, open, onClose, onDone }) {
  const [title, setTitle] = useState("");
  const [ref, setRef] = useState("");
  const [kind, setKind] = useState("policy");
  const [prompt, setPrompt] = useState("");
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTitle(""); setRef(""); setKind("policy"); setPrompt(""); setPreview(""); } }, [open, mode]);
  const genAi = async () => {
    if (!title.trim()) return toast.error("Give it a title first");
    setBusy(true);
    try { const r = await api.post("/policies/ai/draft", { title, prompt }); setPreview(r.data.html); toast.success("Draft generated — review, then create"); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed"); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    if (!title.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      const payload = { title, ref, kind };
      if (mode === "ai") payload.body = { html: preview };
      const r = await api.post("/policies/authoring", payload);
      toast.success("Draft policy created");
      onDone(r.data.document.id);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-policy-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "ai" ? "Create with AI" : "New policy draft"}</DialogTitle>
          <DialogDescription>{mode === "ai"
            ? "Describe your requirement, generate an editable draft, then create it. It stays a draft until you publish."
            : "Creates a draft. It is not visible to staff until you publish it."}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Input data-testid="create-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Lone Working)" />
          <Input data-testid="create-ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Reference (optional — auto-generated if blank)" />
          <select data-testid="create-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm">
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          {mode === "ai" && (<>
            <Textarea data-testid="create-ai-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Context: your trade, sites, who is affected, UK rules to reflect…" className="min-h-[80px]" />
            <Button data-testid="create-ai-generate" variant="outline" disabled={busy} onClick={genAi} className="gap-1"><Sparkle size={15} /> {busy ? "Generating…" : "Generate draft"}</Button>
            {preview && <div className="prose-tmic policy-html border border-slate-200 rounded-lg p-3 max-h-56 overflow-y-auto text-sm" data-testid="create-ai-preview" dangerouslySetInnerHTML={{ __html: preview }} />}
          </>)}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="create-submit" disabled={busy || (mode === "ai" && !preview)} onClick={submit} className="bg-emerald-700 hover:bg-emerald-800">Create draft</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- real Word/PDF file import --------
function ImportFileDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (open) { setFile(null); setTitle(""); setResult(null); } }, [open]);
  const submit = async () => {
    if (!file) return toast.error("Choose a .docx or .pdf file");
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (title.trim()) fd.append("title", title.trim());
      const r = await api.post("/policies/authoring/import-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data.extraction);
      toast.success("Imported as an unassigned draft — original retained");
      onDone(r.data.document.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="import-file-dialog">
        <DialogHeader><DialogTitle>Import a Word or PDF</DialogTitle>
          <DialogDescription>The original file is stored privately and retained. Text is extracted into an editable, unassigned draft. Scanned PDFs are flagged, never turned into a blank policy.</DialogDescription></DialogHeader>
        <div className="flex flex-col gap-3">
          <label className="h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-emerald-400 text-slate-500 text-sm">
            <UploadSimple size={22} />
            <span>{file ? file.name : "Click to choose a .docx or .pdf"}</span>
            <input data-testid="import-file-input" type="file" accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <Input data-testid="import-file-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional — defaults to the file name)" />
          {result && (
            <div data-testid="import-file-result" className={`rounded-lg border p-3 text-sm ${result.scanned ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-emerald-50 border-emerald-200 text-emerald-900"}`}>
              {result.scanned ? <b>Scanned PDF — no text extracted.</b> : <b>Extracted {result.chars} characters{result.pages ? ` from ${result.pages} page(s)` : ""}.</b>}
              <div className="mt-1 text-xs">{result.notes}</div>
              <div className="mt-1 text-xs">Original retained in private storage. Open the draft to review and edit before publishing.</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{result ? "Close" : "Cancel"}</Button>
          {!result && <Button data-testid="import-file-submit" disabled={busy || !file} onClick={submit} className="bg-emerald-700 hover:bg-emerald-800 gap-1"><UploadSimple size={15} /> {busy ? "Importing…" : "Import"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- publish dialog --------
function PublishDialog({ open, onClose, rev, publishedId, onDone }) {
  const [label, setLabel] = useState("");
  const [eff, setEff] = useState("");
  const [fresh, setFresh] = useState(true);
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && rev) {
      setLabel(rev.revision || "");
      setEff(new Date().toISOString().slice(0, 10));
      setFresh(rev.requires_fresh_ack !== false);
      setSummary(rev.change_summary || "");
    }
  }, [open, rev]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/policies/revisions/${rev.id}/publish`, {
        revision: label, effective_date: eff, requires_fresh_ack: fresh,
        change_summary: summary, expected_current_revision_id: publishedId || null,
      });
      toast.success("Published");
      onDone();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not publish"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="publish-dialog">
        <DialogHeader>
          <DialogTitle>Publish revision</DialogTitle>
          <DialogDescription>This becomes the live, immutable version. The previous version is superseded but kept in history.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <label className="text-xs font-semibold text-slate-600">Revision label
            <Input data-testid="publish-label" value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1" />
          </label>
          <label className="text-xs font-semibold text-slate-600">Effective date
            <Input data-testid="publish-effective" type="date" value={eff} onChange={(e) => setEff(e.target.value)} className="mt-1" />
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-700 rounded-lg border border-slate-200 p-3">
            <input data-testid="publish-fresh" type="checkbox" checked={fresh} onChange={(e) => setFresh(e.target.checked)} className="mt-1" />
            <span><b>Everyone must re-acknowledge this version.</b><br />
              <span className="text-xs text-slate-500">Untick only for a minor correction (typo/formatting). Existing acknowledgements will then carry forward.</span></span>
          </label>
          <Textarea data-testid="publish-summary" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="What changed in this revision?" className="min-h-[70px]" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="publish-confirm" disabled={busy} onClick={submit} className="bg-emerald-700 hover:bg-emerald-800 gap-1"><RocketLaunch size={15} /> Publish</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PolicyAuthoring() {
  const [list, setList] = useState([]);
  const [selId, setSelId] = useState(null);
  const [doc, setDoc] = useState(null);          // {document, revisions, section_ids}
  const [html, setHtml] = useState("");
  const [meta, setMeta] = useState({ title: "", ref: "", kind: "policy" });
  const [dirty, setDirty] = useState(false);
  const [createMode, setCreateMode] = useState(null); // 'new' | 'ai'
  const [chooser, setChooser] = useState(false);
  const [importFileOpen, setImportFileOpen] = useState(false);
  const [acting, setActing] = useState(false);
  const [repair, setRepair] = useState(null); // {original, proposed_html}
  const [repairBusy, setRepairBusy] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [compare, setCompare] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewDate, setReviewDate] = useState("");

  const loadList = useCallback(() => api.get("/policies/authoring/drafts").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { loadList(); }, [loadList]);

  const loadDoc = useCallback((id) => {
    api.get(`/policies/authoring/${id}`).then((r) => {
      setDoc(r.data);
      setMeta({ title: r.data.document.title, ref: r.data.document.ref, kind: r.data.document.kind || "policy",
                issue_date: r.data.document.issue_date || "", last_reviewed_date: r.data.document.last_reviewed_date || "",
                review_date: r.data.document.review_date || "", source_status: r.data.document.source_status || "",
                source_approver: r.data.document.source_approver || "" });
      const w = r.data.revisions.find((rv) => ["draft", "review", "approved"].includes(rv.status));
      const base = w || r.data.revisions.find((rv) => rv.is_current) || r.data.revisions[0];
      setHtml(bodyToHtml(base?.body));
      setDirty(false);
    }).catch((e) => toast.error(e?.response?.data?.detail || "Could not load"));
  }, []);
  useEffect(() => { if (selId) loadDoc(selId); }, [selId, loadDoc]);

  const working = doc?.revisions?.find((rv) => ["draft", "review", "approved"].includes(rv.status));
  const published = doc?.revisions?.find((rv) => rv.is_current && rv.status === "published");
  const editable = working && ["draft", "review"].includes(working.status);

  const saveMeta = async () => {
    await api.put(`/policies/authoring/${selId}`, meta);
    toast.success("Details saved");
    loadList(); loadDoc(selId);
  };
  const saveBody = async () => {
    if (!working) return;
    try {
      await api.put(`/policies/revisions/${working.id}`, { body: { html } });
      toast.success("Draft saved");
      setDirty(false); loadDoc(selId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const openRepair = async () => {
    if (!working) return;
    setRepairBusy(true);
    try {
      if (dirty) await api.put(`/policies/revisions/${working.id}`, { body: { html } });
      const r = await api.post(`/policies/${selId}/repair-format`, {});
      setRepair(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not prepare a repair"); }
    finally { setRepairBusy(false); }
  };
  const acceptRepair = async () => {
    try {
      await api.post(`/policies/${selId}/repair-format/accept`, { proposed_html: repair.proposed_html });
      toast.success("Formatting repaired — original text preserved as provenance");
      setRepair(null); loadDoc(selId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply"); }
  };
  const act = async (path, okMsg) => {
    if (acting) return;
    setActing(true);
    try {
      if (dirty && editable) await api.put(`/policies/revisions/${working.id}`, { body: { html } });
      await api.post(path);
      toast.success(okMsg);
      await Promise.all([loadList(), new Promise((res) => { loadDoc(selId); res(); })]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setActing(false); }
  };
  const newRevision = async () => {
    try { await api.post(`/policies/${selId}/revisions/new`, {}); toast.success("New draft revision started"); loadList(); loadDoc(selId); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start revision"); }
  };
  const discardDraft = async () => {
    if (!working) return;
    if (!window.confirm("Discard this unpublished draft? The published version and its acknowledgements are kept.")) return;
    try { await api.delete(`/policies/revisions/${working.id}`); toast.success("Draft discarded"); loadList(); loadDoc(selId); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not discard"); }
  };
  const doCompare = async () => {
    if (!working || !published) return;
    const r = await api.get(`/policies/revisions/compare?a=${published.id}&b=${working.id}`);
    setCompare(r.data);
  };
  const submitReview = async () => {
    try { await api.post(`/policies/${selId}/record-review`, { review_date: reviewDate || null }); toast.success("Review recorded (no change)"); setReviewOpen(false); loadList(); loadDoc(selId); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not record"); }
  };

  return (
    <div className="grid md:grid-cols-[300px_1fr] gap-5" data-testid="policy-authoring">
      {/* list */}
      <div>
        <div className="flex gap-2 mb-3">
          <Button data-testid="create-policy-btn" size="sm" onClick={() => setChooser(true)} className="bg-emerald-700 hover:bg-emerald-800 gap-1 flex-1"><Plus size={15} /> Create policy</Button>
        </div>
        <div className="flex flex-col gap-1.5" data-testid="authoring-list">
          {list.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No documents yet.</p>}
          {list.map((d) => (
            <button key={d.id} data-testid={`author-item-${d.id}`} onClick={() => setSelId(d.id)}
              className={`text-left rounded-lg border p-2.5 ${selId === d.id ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-slate-800 text-sm truncate">{d.title}</span>
                {d.working ? <StatusChip s={d.working.status} /> : d.published ? <StatusChip s="published" /> : <StatusChip s="draft" />}
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5 font-mono">{d.ref}{d.published ? ` · live ${d.published.revision}` : ""}</div>
            </button>
          ))}
        </div>
      </div>

      {/* editor */}
      <div>
        {!doc ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500 text-sm">Select a document, or create a new one.</div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-xs font-semibold text-slate-600">Title
                  <Input data-testid="meta-title" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} className="mt-1" /></label>
                <label className="text-xs font-semibold text-slate-600">Reference
                  <Input data-testid="meta-ref" value={meta.ref} onChange={(e) => setMeta({ ...meta, ref: e.target.value })} className="mt-1" /></label>
                <label className="text-xs font-semibold text-slate-600">Type
                  <select data-testid="meta-kind" value={meta.kind} onChange={(e) => setMeta({ ...meta, kind: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
                    {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select></label>
                <label className="text-xs font-semibold text-slate-600">Issue / start date
                  <Input data-testid="meta-issue" type="date" value={(meta.issue_date || "").slice(0, 10)} onChange={(e) => setMeta({ ...meta, issue_date: e.target.value })} className="mt-1" /></label>
                <label className="text-xs font-semibold text-slate-600">Last reviewed date
                  <Input data-testid="meta-reviewed" type="date" value={(meta.last_reviewed_date || "").slice(0, 10)} onChange={(e) => setMeta({ ...meta, last_reviewed_date: e.target.value })} className="mt-1" /></label>
                <label className="text-xs font-semibold text-slate-600">Next review due
                  <Input data-testid="meta-nextreview" type="date" value={(meta.review_date || "").slice(0, 10)} onChange={(e) => setMeta({ ...meta, review_date: e.target.value })} className="mt-1" /></label>
                <label className="text-xs font-semibold text-slate-600">Source status <span className="text-slate-400">(historical)</span>
                  <Input data-testid="meta-source-status" value={meta.source_status || ""} onChange={(e) => setMeta({ ...meta, source_status: e.target.value })} className="mt-1" /></label>
                <label className="text-xs font-semibold text-slate-600">Source approver <span className="text-slate-400">(historical)</span>
                  <Input data-testid="meta-source-approver" value={meta.source_approver || ""} onChange={(e) => setMeta({ ...meta, source_approver: e.target.value })} className="mt-1" /></label>
                <div className="flex items-end"><Button data-testid="save-meta" size="sm" variant="outline" onClick={saveMeta}>Save details</Button></div>
              </div>
            </div>

            {/* lifecycle bar */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-2" data-testid="lifecycle-bar">
              {published && <span className="text-xs text-slate-500 mr-2">Live: <b>{published.revision}</b></span>}
              {working ? <StatusChip s={working.status} /> : <span className="text-xs text-slate-400">No working revision</span>}
              {working && <span className="text-xs text-slate-500">Working: <b>{working.revision}</b></span>}
              <div className="flex-1" />
              {!working && published && <Button data-testid="new-rev-btn" size="sm" disabled={acting} onClick={newRevision} className="gap-1 bg-slate-800 hover:bg-slate-900"><Plus size={14} /> New revision</Button>}
              {editable && <Button data-testid="save-body-btn" size="sm" variant="outline" disabled={acting} onClick={saveBody}>Save draft</Button>}
              {editable && <Button data-testid="repair-format-btn" size="sm" variant="outline" disabled={acting || repairBusy} onClick={openRepair} className="gap-1 text-blue-700 border-blue-200 hover:bg-blue-50"><MagicWand size={14} /> {repairBusy ? "Preparing…" : "Repair formatting"}</Button>}
              {working?.status === "draft" && <Button data-testid="submit-review-btn" size="sm" disabled={acting} onClick={() => act(`/policies/revisions/${working.id}/submit`, "Sent for review")} className="gap-1 bg-amber-600 hover:bg-amber-700"><PaperPlaneTilt size={14} /> Submit for review</Button>}
              {working?.status === "review" && <Button data-testid="approve-btn" size="sm" disabled={acting} onClick={() => act(`/policies/revisions/${working.id}/approve`, "Approved")} className="gap-1 bg-blue-600 hover:bg-blue-700"><SealCheck size={14} /> Approve</Button>}
              {working && ["review", "approved"].includes(working.status) && <Button data-testid="return-draft-btn" size="sm" variant="outline" disabled={acting} onClick={() => act(`/policies/revisions/${working.id}/return`, "Returned to draft")} className="gap-1"><ArrowUUpLeft size={14} /> Return to draft</Button>}
              {working?.status === "approved" && <Button data-testid="publish-btn" size="sm" disabled={acting} onClick={() => setPublishOpen(true)} className="gap-1 bg-emerald-700 hover:bg-emerald-800"><RocketLaunch size={14} /> Publish</Button>}
              {working && published && <Button data-testid="compare-btn" size="sm" variant="outline" onClick={doCompare} className="gap-1"><GitDiff size={14} /> Compare</Button>}
              {working && <Button data-testid="discard-draft-btn" size="sm" variant="outline" onClick={discardDraft} className="gap-1 text-red-600 border-red-200 hover:bg-red-50"><Trash size={14} /> Discard draft</Button>}
              <Button data-testid="record-review-btn" size="sm" variant="outline" onClick={() => setReviewOpen(true)} className="gap-1"><ClockCounterClockwise size={14} /> Reviewed, no change</Button>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              {!editable && working?.status === "approved" && <p className="text-xs text-blue-700 mb-3">This revision is approved and locked. Return it to draft to edit, or publish it.</p>}
              {!working && <p className="text-xs text-slate-500 mb-3">The live content is shown read-only. Start a new revision to make changes.</p>}
              <RichTextEditor html={html} disabled={!editable} onChange={(h) => { setHtml(h); setDirty(true); }} />
            </div>
          </div>
        )}
      </div>

      <CreateChooser open={chooser} onClose={() => setChooser(false)}
        onPick={(m) => { setChooser(false); if (m === "import-file") setImportFileOpen(true); else setCreateMode(m); }} />
      <CreateDialog mode={createMode || "new"} open={!!createMode} onClose={() => setCreateMode(null)} onDone={(id) => { loadList(); setSelId(id); }} />
      <ImportFileDialog open={importFileOpen} onClose={() => setImportFileOpen(false)} onDone={(id) => { loadList(); setSelId(id); }} />
      {working && <PublishDialog open={publishOpen} onClose={() => setPublishOpen(false)} rev={working} publishedId={published?.id} onDone={() => { loadList(); loadDoc(selId); }} />}

      <Dialog open={!!compare} onOpenChange={(o) => !o && setCompare(null)}>
        <DialogContent data-testid="compare-dialog" className="max-w-3xl">
          <DialogHeader><DialogTitle>Compare {compare?.a?.revision} → {compare?.b?.revision}</DialogTitle>
            <DialogDescription>{compare ? `${compare.diff.added} added · ${compare.diff.changed} changed · ${compare.diff.removed} removed` : ""}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto flex flex-col gap-1.5">
            {compare?.diff.rows.map((r, i) => (
              <div key={i} data-testid={`diff-row-${i}`} className={`rounded p-2 text-sm ${r.status === "added" ? "bg-emerald-50" : r.status === "removed" ? "bg-red-50" : r.status === "changed" ? "bg-amber-50" : "bg-slate-50"}`}>
                <span className="text-[10px] font-bold uppercase text-slate-400">{r.status}</span>
                {r.a && <div className={r.status === "changed" ? "line-through text-slate-400" : "text-slate-700"}>{r.a}</div>}
                {r.b && <div className="text-slate-800">{r.b}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!repair} onOpenChange={(o) => !o && setRepair(null)}>
        <DialogContent data-testid="repair-dialog" className="max-w-4xl">
          <DialogHeader><DialogTitle>Repair formatting</DialogTitle>
            <DialogDescription>Structure only — wording, numbering and meaning are unchanged. Review both sides, then accept to replace the draft body. The original text is kept as provenance and you can still edit in the rich editor.</DialogDescription></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
            <div><div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Original (as imported)</div>
              <pre data-testid="repair-original" className="whitespace-pre-wrap text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">{repair?.original}</pre></div>
            <div><div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Proposed structure</div>
              <div data-testid="repair-proposed" className="prose-tmic policy-html text-sm bg-white border border-slate-200 rounded-lg p-3" dangerouslySetInnerHTML={{ __html: repair?.proposed_html || "" }} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepair(null)}>Cancel</Button>
            <Button data-testid="repair-accept" onClick={acceptRepair} className="bg-blue-700 hover:bg-blue-800">Accept & replace draft body</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reviewOpen} onOpenChange={(o) => !o && setReviewOpen(false)}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader><DialogTitle>Record review — no change</DialogTitle>
            <DialogDescription>Logs that this document was reviewed and remains correct, and sets the next review date. No new revision is created.</DialogDescription>
          </DialogHeader>
          <label className="text-xs font-semibold text-slate-600">Next review date
            <Input data-testid="review-date" type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} className="mt-1" /></label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>Cancel</Button>
            <Button data-testid="review-confirm" onClick={submitReview} className="bg-emerald-700 hover:bg-emerald-800">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
